import Head from 'next/head';
import Link from 'next/link';
import {createClient} from 'contentful';
import React, {useState} from 'react';

const client = createClient({
    space: process.env.CONTENTFUL_SPACE_ID,
    accessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
});

export async function getStaticProps() {
    const res = await client.getEntries({content_type: 'blogPost'});

    return {
        props: {
            posts: res.items,
        },
        revalidate: 1,
    }
}

export default function Blog({posts}) {
    const [firstPost, ...remainingPosts] = posts;
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 6;
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = remainingPosts.slice(indexOfFirstPost, indexOfLastPost);  // Change here
    const totalPages = Math.ceil(remainingPosts.length / postsPerPage);  // Change here

    const handleNextPage = (e) => {
        e.preventDefault();
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = (e) => {
        e.preventDefault();
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    const schemaMarkup = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Blog | deinSEOExpert",
        "description": "Dies ist der offizielle Blog von deinSEOExpert. Hier teilen wir unsere neuesten Nachrichten, Tipps und Anleitungen zu SEO.",
        "url": "https://www.deinseoexpert.de/blog",
        "publisher": {
            "@type": "Organization",
            "name": "deinSEOExpert"
        }
    });

    return (
        <section className="bg-white dark:bg-gray-900">
            <Head>
                <title>Blog | deinSEOExpert</title>
                <link rel="icon" href="/favicon.ico"/>
                <meta
                    name="description"
                    content="Entdecken Sie unsere SEO-Agentur und erfahren Sie mehr über unsere professionellen SEO-Dienstleistungen. Besuchen Sie unseren Blog für kompetente Beratung und Expertenwissen in Sachen SEO."
                />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaMarkup }} />
            </Head>
            <div className="py-8 px-4 mx-auto max-w-screen-xl lg:py-16">
                <div
                    className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 md:p-12 mb-8">
                    <a className="bg-blue-100 text-blue-800 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded-md dark:bg-gray-700 dark:text-blue-400 mb-2">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"
                             xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                                d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"></path>
                        </svg>
                        {firstPost.fields.category}
                    </a>
                    <h1 className="text-gray-900 dark:text-white text-3xl md:text-5xl font-extrabold mb-2">{firstPost.fields.title}</h1>
                    <p className="text-lg font-normal text-gray-500 dark:text-gray-400 mb-6">{firstPost.fields.summary}</p>
                    <p className="text-lg font-normal text-gray-500 dark:text-gray-400 mb-4">{firstPost.fields.zusammenfassung}</p>
                    <Link href={`/blog/${firstPost.fields.slug}`} className="inline-flex justify-center items-center py-2.5 px-5 text-base font-medium text-center text-white rounded-lg bg-blue-900 hover:bg-blue-600 focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-900">
                            Read more
                            <svg aria-hidden="true" className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd"
                                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                                      clipRule="evenodd"></path>
                            </svg>

                    </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                    {currentPosts.map((post, index) => (
                        <div
                            className={`bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 md:p-12${index === 0 ? ' mb-8' : ''}`}
                            key={post.sys.id}>
                            <a className="bg-green-100 text-green-800 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded-md dark:bg-gray-700 dark:text-green-400 mb-2">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"
                                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path clipRule="evenodd" fillRule="evenodd"
                                          d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z"></path>
                                </svg>
                                {post.fields.category}
                            </a>
                            <h2 className="text-gray-900 dark:text-white text-3xl font-extrabold mb-2">{post.fields.title}</h2>
                            <p className="text-lg font-normal text-gray-500 dark:text-gray-400 mb-4">{post.fields.summary}</p>
                            <p className="text-lg font-normal text-gray-500 dark:text-gray-400 mb-4">{post.fields.zusammenfassung}</p>
                            <Link href={`/blog/${post.fields.slug}`} className="text-blue-900 dark:text-blue-500 hover:underline font-medium text-lg inline-flex items-center">
                                    Read more
                                    <svg aria-hidden="true" className="w-4 h-4 ml-2" fill="none" stroke="currentColor"
                                         strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                              d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                                    </svg>
                            </Link>
                        </div>
                    ))}
                </div>
                <nav className=" mt-10 text-center" aria-label="Page navigation example">
                    <ul className="inline-flex -space-x-px">
                        <li>
                            <a
                                href="#"
                                className="px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </a>
                        </li>
                        {Array.from({length: totalPages}, (_, index) => (
                            <li key={index}>
                                <a
                                    href="#"
                                    className={`px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 ${
                                        currentPage === index + 1
                                            ? 'text-blue-600 border-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white'
                                            : 'hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                                    }`}
                                    onClick={() => setCurrentPage(index + 1)}
                                >
                                    {index + 1}
                                </a>
                            </li>
                        ))}
                        <li>
                            <a
                                href="#"
                                className="px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </section>
    );
}
